// src/store/modules/auth.js
import authService from '@/services/authService'; // API servisi
import userService from '@/services/userService'; // Assuming this exists for profile updates
import router from '@/router'; // Yönlendirme için router

// Helper to set token in Axios headers and localStorage
const setAuthData = (commit, token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    // Axios header'ı da ayarlayabiliriz (api.js'deki interceptor bunu zaten yapmalı)
    // Assuming your apiClient in authService uses an interceptor to set the header
    commit('SET_TOKEN', token);
    commit('SET_USER', user);
};

const clearAuthData = (commit) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Assuming your apiClient interceptor clears the header on logout/error
    commit('CLEAR_AUTH');
};

export default {
  namespaced: true, // Modül isim alanı kullan
  state: {
    token: localStorage.getItem('token') || null,
    user: JSON.parse(localStorage.getItem('user')) || null,
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_USER(state, user) {
      state.user = user;
    },
    CLEAR_AUTH(state) {
      state.token = null;
      state.user = null;
    },
    UPDATE_USER_PROFILE(state, updatedData) {
        if (state.user) {
            state.user = { ...state.user, ...updatedData };
            localStorage.setItem('user', JSON.stringify(state.user));
        }
    }
  },
  actions: {
    async login({ commit }, credentials) {
      commit('SET_LOADING', true, { root: true });
      commit('CLEAR_ERROR', null, { root: true }); // Clear previous errors
      try {
        const response = await authService.login(credentials);
        const { token, user } = response.data;
        setAuthData(commit, token, user);

        // Giriş yaptıktan sonra yönlendir (eğer redirect query varsa oraya)
        const redirectPath = router.currentRoute.value.query.redirect || '/';
        router.push(redirectPath);
        return response; // Return response for potential component use

      } catch (error) {
        const errorData = error.response?.data;
        const errorMessage = errorData?.message || 'Giriş sırasında bir hata oluştu.';

        // --- Handle Verification Error Specifically ---
        if (errorData?.code === 'ACCOUNT_NOT_VERIFIED') {
            commit('SET_ERROR', errorMessage, { root: true });
            // DO NOT clear auth data here
        } else {
            // For other errors, clear auth data
            commit('SET_ERROR', errorMessage, { root: true });
            clearAuthData(commit);
        }
        // --- End Verification Handling ---

        console.error("Vuex Login Action Error:", error.response || error);
        throw error; // Re-throw the error so the component's catch block can execute
      } finally {
        commit('SET_LOADING', false, { root: true });
      }
    },

    async register({ commit }, userData) {
       commit('SET_LOADING', true, { root: true });
       commit('CLEAR_ERROR', null, { root: true });
       try {
         const response = await authService.register(userData);
         // --- REGISTRATION SUCCESS - DO NOT LOGIN ---
         // The backend now sends a message like "Please check your email..."
         // We will let the component handle displaying this message.
         // We DO NOT set auth data or redirect to home here.
         // --- End Change ---

         // Optionally, redirect to login page with a query param,
         // but the component approach of showing message directly is also fine.
         // router.push({ name: 'Login', query: { registered: 'success' } }); // Example

         return response; // Return the response (contains the success message)
       } catch (error) {
         const errorMessage = error.response?.data?.message || 'Kayıt sırasında bir hata oluştu.';
         commit('SET_ERROR', errorMessage, { root: true });
         console.error("Vuex Register Action Error:", error.response || error);
         throw error; // Re-throw error for component handling
       } finally {
         commit('SET_LOADING', false, { root: true });
       }
    },

    logout({ commit }) {
      clearAuthData(commit);
      // Ensure redirect happens after state is cleared
      router.push({ name: 'Login' }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
              console.error('Router push error during logout:', err);
          }
      });
    },

    async tryAutoLogin({ commit, state }) {
        const token = state.token;
        if (!token) {
            return false;
        }
        // Optional: Add client-side JWT expiration check here if desired
        try {
            // Use /me endpoint to validate token and get fresh user data
            const response = await authService.getMe();
            const user = response.data;
            // Re-set data to ensure consistency and update localStorage
            setAuthData(commit, token, user); // Use helper to ensure consistency
            return true;
        } catch (error) {
            console.error("Auto-login failed (token likely invalid/expired):", error.response || error);
            clearAuthData(commit); // Clear invalid token/user data
            return false;
        }
    },

    handleAuthCallback({ commit, dispatch }, token) { // Added dispatch
         // Store the token immediately
         localStorage.setItem('token', token);
         commit('SET_TOKEN', token);
         // Use tryAutoLogin (which calls /me) to fetch user data and verify token
         return dispatch('tryAutoLogin'); // Use dispatch to call another action
    },

     async updateProfile({ commit, state }, updatedData) { // Added state
        if (!state.user) {
             commit('SET_ERROR', 'Profil güncellemek için giriş yapmalısınız.', { root: true });
             throw new Error('User not authenticated');
        }
        commit('SET_LOADING', true, { root: true });
        commit('CLEAR_ERROR', null, { root: true });
         try {
             // Assuming userService.updateProfile sends request for the logged-in user
             const response = await userService.updateProfile(updatedData);
             commit('UPDATE_USER_PROFILE', response.data);
             return response.data;
         } catch (error) {
             const errorMessage = error.response?.data?.message || 'Profil güncelleme hatası.';
             commit('SET_ERROR', errorMessage, { root: true });
             console.error("Vuex UpdateProfile Action Error:", error.response || error);
             throw error;
         } finally {
             commit('SET_LOADING', false, { root: true });
         }
     },

     async forgotPassword({ commit }, emailData) {
      commit('SET_LOADING', true, { root: true });
      commit('CLEAR_ERROR', null, { root: true });
      try {
          // Service call returns the full response
          const response = await authService.forgotPassword(emailData);
          // You might commit a success message to global state if needed
          // commit('SET_SUCCESS_MESSAGE', response.data.message, { root: true });
          return response; // Return response for component use
      } catch (error) {
          const errorMessage = error.response?.data?.message || 'Şifre sıfırlama isteği gönderilemedi.';
          commit('SET_ERROR', errorMessage, { root: true });
          console.error("Vuex ForgotPassword Action Error:", error.response || error);
          throw error; // Re-throw for component handling
      } finally {
          commit('SET_LOADING', false, { root: true });
      }
  },

  async resetPassword({ commit }, { token, passwordData }) {
      commit('SET_LOADING', true, { root: true });
      commit('CLEAR_ERROR', null, { root: true });
      try {
          const response = await authService.resetPassword(token, passwordData);
          // commit('SET_SUCCESS_MESSAGE', response.data.message, { root: true });
          return response;
      } catch (error) {
          const errorMessage = error.response?.data?.message || 'Şifre güncellenemedi.';
          commit('SET_ERROR', errorMessage, { root: true });
          console.error("Vuex ResetPassword Action Error:", error.response || error);
          throw error;
      } finally {
          commit('SET_LOADING', false, { root: true });
      }
  }



  },
  getters: {
    isAuthenticated: state => !!state.token,
    currentUser: state => state.user,
    userRole: state => state.user?.role,
    userId: state => state.user?.id,
  }
}
